import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Driver,
  DriverListItem,
  AvailableDriver,
  DriverStatistics,
  DriverGpsUpdate,
} from '../models/driver.model';
import { ApiResponse } from '../interfaces';

/**
 * DriversService
 * 
 * Service for driver management in dispatcher panel
 * Handles driver data, GPS positions, availability
 */
@Injectable({
  providedIn: 'root'
})
export class DriversService {
  private readonly apiUrl = `${environment.apiUrl}/company/drivers`;

  constructor(private http: HttpClient) {}

  /**
   * Get list of drivers
   * 
   * @param page - Page number
   * @param limit - Items per page
   * @param filters - Optional filters
   */
  getDrivers(
    page: number = 1,
    limit: number = 100,
    filters?: {
      status?: string;
      isOnline?: boolean;
      search?: string;
    }
  ): Observable<ApiResponse<{ drivers: Driver[]; total: number }>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (filters) {
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.isOnline !== undefined) {
        params = params.set('isOnline', filters.isOnline.toString());
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
    }

    return this.http.get<ApiResponse<{ drivers: Driver[]; total: number }>>(
      this.apiUrl,
      { params }
    );
  }

  /**
   * Get single driver details
   * 
   * @param driverId - Driver ID
   */
  getDriverById(driverId: number): Observable<ApiResponse<Driver>> {
    return this.http.get<ApiResponse<Driver>>(`${this.apiUrl}/${driverId}`);
  }

  /**
   * Get available drivers for order assignment
   * 
   * @param pickupLat - Pickup latitude
   * @param pickupLng - Pickup longitude
   * @param radius - Search radius in km
   * @param maxResults - Maximum drivers to return
   */
  getAvailableDrivers(
    pickupLat: number,
    pickupLng: number,
    radius: number = 5,
    maxResults: number = 20
  ): Observable<ApiResponse<AvailableDriver[]>> {
    let params = new HttpParams()
      .set('pickupLat', pickupLat.toString())
      .set('pickupLng', pickupLng.toString())
      .set('radius', radius.toString())
      .set('maxResults', maxResults.toString());

    return this.http.get<ApiResponse<AvailableDriver[]>>(
      `${this.apiUrl}/available`,
      { params }
    );
  }

  /**
   * Get current GPS positions of all online drivers
   */
  getDriverPositions(): Observable<ApiResponse<DriverGpsUpdate[]>> {
    return this.http.get<ApiResponse<DriverGpsUpdate[]>>(`${this.apiUrl}/positions`);
  }

  /**
   * Get driver GPS position by ID
   * 
   * @param driverId - Driver ID
   */
  getDriverPosition(driverId: number): Observable<ApiResponse<DriverGpsUpdate>> {
    return this.http.get<ApiResponse<DriverGpsUpdate>>(`${this.apiUrl}/${driverId}/position`);
  }

  /**
   * Get driver statistics
   * 
   * @param driverId - Driver ID
   * @param dateFrom - Start date (optional)
   * @param dateTo - End date (optional)
   */
  getDriverStatistics(
    driverId: number,
    dateFrom?: Date,
    dateTo?: Date
  ): Observable<ApiResponse<DriverStatistics>> {
    let params = new HttpParams();

    if (dateFrom) {
      params = params.set('dateFrom', dateFrom.toISOString());
    }
    if (dateTo) {
      params = params.set('dateTo', dateTo.toISOString());
    }

    return this.http.get<ApiResponse<DriverStatistics>>(
      `${this.apiUrl}/${driverId}/statistics`,
      { params }
    );
  }

  /**
   * Get driver's active order
   * 
   * @param driverId - Driver ID
   */
  getDriverActiveOrder(driverId: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/${driverId}/active-order`);
  }

  /**
   * Send message to driver
   * 
   * @param driverId - Driver ID
   * @param message - Message text
   */
  sendMessage(driverId: number, message: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.apiUrl}/${driverId}/send-message`,
      { message }
    );
  }

  /**
   * Call driver (initiate phone call)
   * 
   * @param driverId - Driver ID
   */
  callDriver(driverId: number): Observable<ApiResponse<{ phone: string }>> {
    return this.http.post<ApiResponse<{ phone: string }>>(
      `${this.apiUrl}/${driverId}/call`,
      {}
    );
  }

  /**
   * Set driver status (online/offline/busy)
   * 
   * @param driverId - Driver ID
   * @param status - New status
   */
  setDriverStatus(driverId: number, status: string): Observable<ApiResponse<Driver>> {
    return this.http.put<ApiResponse<Driver>>(
      `${this.apiUrl}/${driverId}/status`,
      { status }
    );
  }

  /**
   * Get driver rating history
   * 
   * @param driverId - Driver ID
   * @param page - Page number
   * @param limit - Items per page
   */
  getDriverRatings(
    driverId: number,
    page: number = 1,
    limit: number = 20
  ): Observable<ApiResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/${driverId}/ratings`,
      { params }
    );
  }

  /**
   * Get online drivers count
   */
  getOnlineDriversCount(): Observable<ApiResponse<{ count: number }>> {
    return this.http.get<ApiResponse<{ count: number }>>(`${this.apiUrl}/online-count`);
  }

  /**
   * Search drivers by name, phone, or car number
   * 
   * @param query - Search query
   * @param limit - Maximum results
   */
  searchDrivers(query: string, limit: number = 10): Observable<ApiResponse<DriverListItem[]>> {
    let params = new HttpParams()
      .set('q', query)
      .set('limit', limit.toString());

    return this.http.get<ApiResponse<DriverListItem[]>>(
      `${this.apiUrl}/search`,
      { params }
    );
  }
}
